import { useCallback, useEffect, useRef, useState } from "react";
import { MapHistoryState } from "./useGaiaMapContext";
import L from "leaflet";
import _ from "lodash";

export const useMapHistory = () => {
    const history = useRef<MapHistoryState[]>([]);
    const currentIndex = useRef<number>(-1);
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);

    const updateHistoryState = useCallback(() => {
        setCanUndo(currentIndex.current > 0 && history.current.length > 1);
        setCanRedo(currentIndex.current < history.current.length - 1);
    }, []);

    useEffect(() => {
        if (history.current.length === 0) {
            const initialState: MapHistoryState = {
                points: [],
                featureGroup: new L.FeatureGroup(),
                drawMode: null,
                isDrawing: false,
                isComplete: false,
            };
            history.current = [initialState];
            currentIndex.current = 0;
            updateHistoryState();
        }
    }, [updateHistoryState]);

    const cloneLayer = useCallback((layer: L.Layer) => {
        let clonedLayer: L.Layer | null = null;

        if (layer instanceof L.Marker && !(layer instanceof L.CircleMarker)) {
            // Standard icon marker
            clonedLayer = L.marker(layer.getLatLng(), { ...(layer as any).options });
            const timestamp = (layer as any)._addedTimestamp || Date.now();
            (clonedLayer as any)._addedTimestamp = timestamp;
            // preserve custom metadata
            (clonedLayer as any)._gaiaRole = (layer as any)._gaiaRole;
            (clonedLayer as any)._originalBoundary = (layer as any)._originalBoundary;
            (clonedLayer as any)._initialBoundary = (layer as any)._initialBoundary;
        } else if (layer instanceof L.CircleMarker) {
            // Circle marker (used for points and editing vertices)
            const latLng = layer.getLatLng();
            clonedLayer = L.circleMarker(latLng, { ...(layer as any).options } as any);
            const timestamp = (layer as any)._addedTimestamp || Date.now();
            (clonedLayer as any)._addedTimestamp = timestamp;
            // preserve custom metadata so cleanup/logic keeps behavior
            (clonedLayer as any)._gaiaRole = (layer as any)._gaiaRole;
            (clonedLayer as any)._originalBoundary = (layer as any)._originalBoundary;
            (clonedLayer as any)._initialBoundary = (layer as any)._initialBoundary;
        } else if ((layer as any)._isCirclePolygon) {
            const latLngs = (layer as any).getLatLngs();
            clonedLayer = L.polygon(latLngs as L.LatLngExpression[], { ...(layer as any).options });
            (clonedLayer as any)._isCirclePolygon = true;
        } else if (layer instanceof L.Polyline) {
            const latLngs = layer.getLatLngs();
            if (layer instanceof L.Polygon) {
                clonedLayer = L.polygon(latLngs as L.LatLngExpression[], { ...(layer as any).options });
            } else {
                clonedLayer = L.polyline(latLngs as L.LatLngExpression[], { ...(layer as any).options });
            }
        }

        if (clonedLayer) {
            // Copy event handlers
            if ((layer as any)._events) {
                for (const eventType in (layer as any)._events) {
                    const handlers = (layer as any)._events[eventType];
                    if (Array.isArray(handlers)) {
                        handlers.forEach((handler) => {
                            clonedLayer?.on(eventType, handler.fn || handler);
                        });
                    } else if (handlers) {
                        clonedLayer?.on(eventType, handlers.fn || handlers);
                    }
                }
            }
        }

        return clonedLayer;
    }, []);

    const pushHistory = useCallback(
        (state: Omit<MapHistoryState, "isDrawing">, isDrawing = false, isInitialState = false) => {
            if (isInitialState) {
                const clonedState = _.cloneDeep(state);
                const initialState: MapHistoryState = {
                    ...clonedState,
                    isDrawing: false,
                };
                history.current = [initialState];
                currentIndex.current = 0;
                updateHistoryState();
                return;
            }

            // Clone the feature group
            const featureGroupClone = new L.FeatureGroup();
            if (state.featureGroup) {
                state.featureGroup.eachLayer((layer) => {
                    const clonedLayer = cloneLayer(layer);
                    if (clonedLayer) {
                        featureGroupClone.addLayer(clonedLayer);
                    }
                });
            }

            // Check if this state completes a shape
            const hasNewPolygon = featureGroupClone.getLayers().some((layer) => layer instanceof L.Polygon);
            const isComplete = (state as any).isComplete || (!isDrawing && hasNewPolygon);

            // Create the new state
            const newState: MapHistoryState = {
                points: [...state.points],
                featureGroup: featureGroupClone,
                drawMode: state.drawMode,
                isDrawing,
                isComplete,
            };

            // Always add the new state to history
            if (currentIndex.current < history.current.length - 1) {
                // If we're in the middle of the history, remove future states
                history.current = history.current.slice(0, currentIndex.current + 1);
            }

            // If we're completing a shape and we were drawing
            if (isComplete && !isDrawing && history.current[currentIndex.current]?.isDrawing) {
                let drawingStartIndex = currentIndex.current;
                const currentLayerCount = featureGroupClone.getLayers().length;

                // Find where this shape's drawing started
                while (drawingStartIndex > 0) {
                    const prevState = history.current[drawingStartIndex - 1];
                    const prevLayerCount = prevState.featureGroup.getLayers().length;
                    if (!prevState.isDrawing || prevLayerCount !== currentLayerCount - 1) {
                        break;
                    }
                    drawingStartIndex--;
                }

                // Keep all states before the drawing started, plus our new state
                history.current = [...history.current.slice(0, drawingStartIndex), newState];
            } else {
                // For all other states, just add to history
                history.current.push(newState);
            }

            currentIndex.current = history.current.length - 1;
            updateHistoryState();
        },
        [cloneLayer, updateHistoryState]
    );

    const getUndoState = useCallback(() => {
        if (currentIndex.current > 0) {
            currentIndex.current--;
            updateHistoryState();
            return history.current[currentIndex.current];
        }
        return null;
    }, [updateHistoryState]);

    const getRedoState = useCallback(() => {
        if (currentIndex.current < history.current.length - 1) {
            currentIndex.current++;
            updateHistoryState();
            return history.current[currentIndex.current];
        }
        return null;
    }, [updateHistoryState]);

    return {
        pushHistory,
        getUndoState,
        getRedoState,
        canUndo,
        canRedo,
    };
};
